import { Link, type LinkOptions } from '@tanstack/react-router'
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cx } from './cx'

const tabClass =
  'relative inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap px-0.5 text-small font-medium text-ink-2 transition-colors duration-150 ' +
  'hover:text-ink after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors ' +
  'focus-visible:outline-offset-[-2px]'

const activeClass = 'text-ink after:bg-accent'

function Count({ value }: { value: number }) {
  return <span className="rounded-full bg-bg-hover px-1.5 text-label leading-5 tabular text-ink-2">{value}</span>
}

export type TabLinkItem = LinkOptions & { label: ReactNode; count?: number }

/** Sub-navigation under a PageHeader. Each tab is a real router link (aria-current on the active one). */
export function TabLinks({ items, label, className }: { items: TabLinkItem[]; label: string; className?: string }) {
  return (
    <nav aria-label={label} className={cx('-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0', className)}>
      <div className="flex gap-6 border-b border-line">
        {items.map(({ label: text, count, ...link }, i) => (
          <Link
            key={i}
            {...link}
            className={tabClass}
            activeProps={{ className: activeClass }}
            activeOptions={{ includeSearch: true, ...link.activeOptions }}
          >
            {text}
            {count !== undefined && <Count value={count} />}
          </Link>
        ))}
      </div>
    </nav>
  )
}

export type TabItem<V extends string> = { id: V; label: ReactNode; count?: number; panel: ReactNode }

type TabsProps<V extends string> = {
  tabs: TabItem<V>[]
  label: string
  value?: V
  defaultValue?: V
  onChange?: (value: V) => void
  className?: string
}

/** In-page tabs (local state) with the WAI-ARIA tabs pattern: arrows/Home/End move, automatic activation. */
export function Tabs<V extends string>({ tabs, label, value, defaultValue, onChange, className }: TabsProps<V>) {
  const base = useId()
  const [internal, setInternal] = useState<V | undefined>(defaultValue ?? tabs[0]?.id)
  const current = value ?? internal
  const listRef = useRef<HTMLDivElement>(null)

  function select(id: V) {
    if (value === undefined) setInternal(id)
    onChange?.(id)
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const i = tabs.findIndex((t) => t.id === current)
    const next =
      e.key === 'ArrowRight' ? (i + 1) % tabs.length
      : e.key === 'ArrowLeft' ? (i - 1 + tabs.length) % tabs.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? tabs.length - 1
      : -1
    const tab = tabs[next]
    if (!tab) return
    e.preventDefault()
    select(tab.id)
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role=tab]')[next]?.focus()
  }

  return (
    <div className={className}>
      <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
        <div ref={listRef} role="tablist" aria-label={label} className="flex gap-6 border-b border-line">
          {tabs.map((t) => {
            const selected = t.id === current
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`${base}-tab-${t.id}`}
                aria-selected={selected}
                aria-controls={`${base}-panel-${t.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => select(t.id)}
                onKeyDown={onKeyDown}
                className={cx(tabClass, selected && activeClass)}
              >
                {t.label}
                {t.count !== undefined && <Count value={t.count} />}
              </button>
            )
          })}
        </div>
      </div>
      {tabs.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={`${base}-panel-${t.id}`}
          aria-labelledby={`${base}-tab-${t.id}`}
          hidden={t.id !== current}
          tabIndex={0}
          className="pt-6 focus-visible:outline-offset-4"
        >
          {t.id === current && t.panel}
        </div>
      ))}
    </div>
  )
}
